import type { RequestStream } from '@talos/kyoko';
import {
  CARE_ORDER,
  ORDER,
  QUOTE,
  SecurityTypesUtils,
  useCombinedErrorObs,
  useSecuritiesContext,
  useSubscription,
  useSyncedRef,
  WORKING_ORDER,
  wsScanToMap,
  type CareOrder,
  type MinimalSubscriptionResponse,
  type Order,
  type Quote,
} from '@talos/kyoko';
import { minBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { combineLatest, map, shareReplay, startWith, type Observable } from 'rxjs';
import {
  createCareOrderBlotterEntity,
  LegRow,
  OrderRow,
  QuoteRow,
  type CareOrderBlotterEntity,
  type OrderWithParentOrderID,
  type QuoteWithParentRFQID,
} from './types';

export const useCareOrderBlotterDataObs = (enableCareOrderNetting = false) => {
  const { data: careOrdersObs, error: careOrderErrors } = useSubscription<CareOrder>({
    name: CARE_ORDER,
    tag: 'CareOrder blotter',
  });
  const { data: workingOrdersObs, error: workingOrderErrors } = useSubscription<CareOrder>(
    enableCareOrderNetting
      ? {
          name: WORKING_ORDER,
          tag: 'CareOrder blotter',
        }
      : null
  );

  // First we need to get all care orders by ID...
  const careOrdersByIDObs = useMemo(
    () =>
      careOrdersObs.pipe(
        wsScanToMap({
          getUniqueKey: order => order.OrderID,
          newMapEachUpdate: false,
          waitForAllPagesToLoad: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [careOrdersObs]
  );
  const workingOrdersByIDObs = useMemo(
    () =>
      workingOrdersObs.pipe(
        wsScanToMap({
          getUniqueKey: order => order.OrderID,
          newMapEachUpdate: false,
          waitForAllPagesToLoad: true,
        }),
        startWith(new Map<string, CareOrder>()),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [workingOrdersObs]
  );

  const [quotesRequest, setQuotesRequest] = useState<RequestStream | null>(null);
  const { data: quotesObs, error: quoteErrors } = useSubscription<Quote>(quotesRequest);

  const [ordersRequest, setOrdersRequest] = useState<RequestStream | null>(null);
  const { data: ordersObs, error: orderErrors } = useSubscription<Order>(ordersRequest);

  useEffect(() => {
    const subscription = careOrdersByIDObs.subscribe(careOrdersByID => {
      const startDate = minBy(Array.from(careOrdersByID.values()), 'SubmitTime')?.SubmitTime;
      setQuotesRequest({
        name: QUOTE,
        tag: 'CareOrder blotter',
        StartDate: startDate,
      });
      setOrdersRequest({
        name: ORDER,
        tag: 'CareOrder blotter',
        StartDate: startDate,
      });
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [careOrdersByIDObs]);

  const quotesByRFQIDObs = useMemo(
    () => quotesObs.pipe(wsScanToMap({ getUniqueKey: d => d.RFQID, newMapEachUpdate: false })),
    [quotesObs]
  );
  const ordersByOrderIDObs = useMemo(
    () => ordersObs.pipe(wsScanToMap({ getUniqueKey: d => d.OrderID, newMapEachUpdate: false })),
    [ordersObs]
  );

  const { securitiesBySymbol } = useSecuritiesContext();
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol); // make it stable so we dont rebuild the pipe when we get changes

  // Finally, merge the care orders and the OMS orders
  const combinedDataObs = useMemo<Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>>(
    () =>
      combineLatest([careOrdersByIDObs, workingOrdersByIDObs, quotesByRFQIDObs, ordersByOrderIDObs]).pipe(
        map(([careOrdersByID, workingOrdersByID, quotesByRFQID, ordersByOrderID]) => {
          const blotterEntitiesByID = new Map();

          // The order of loops here is important!

          // First, we add all care orders to the map
          for (const careOrder of careOrdersByID.values()) {
            if (careOrder.WorkingOrderID != null) {
              // Care Orders that are netted into some WorkingOrder are dropped
              continue;
            }
            blotterEntitiesByID.set(careOrder.OrderID, createCareOrderBlotterEntity(careOrder));
          }

          // Then, we add all working orders to the map.
          // For now, they share CareOrder semantics and render as care-order rows.
          for (const workingOrder of workingOrdersByID.values()) {
            blotterEntitiesByID.set(workingOrder.OrderID, createCareOrderBlotterEntity(workingOrder));
          }

          // Then, we add quotes that are children of any care order
          for (const quote of quotesByRFQID.values()) {
            if (quote.ParentRFQID != null) {
              if (!blotterEntitiesByID.has(quote.ParentRFQID)) {
                continue;
              }

              const quoteRow = new QuoteRow(quote as QuoteWithParentRFQID);
              blotterEntitiesByID.set(quote.RFQID, quoteRow);

              // Only compound-security quotes render level-2 leg rows.
              const security = securitiesBySymbolRef.current.get(quoteRow.Symbol);
              const isCompoundSecurity = SecurityTypesUtils.Compound.isType(security);
              // Multi-token quotes have one leg per market per symbol. We only render unique legs per symbol.
              if (isCompoundSecurity && quoteRow.legsRepresentative.length > 0) {
                for (const representativeLeg of quoteRow.legsRepresentative) {
                  const legRow = new LegRow({ quote: quoteRow, representativeLeg });
                  blotterEntitiesByID.set(legRow.rowID, legRow);
                }
              }
            }
          }

          // Then, we add orders that are children of a care order (and possibly also a quote for RFQ orders)
          for (const order of ordersByOrderID.values()) {
            // Check whether this order belongs to a quote
            if (order.RFQID != null) {
              if (!blotterEntitiesByID.has(order.RFQID)) {
                // If we got here, the RFQ doesn't exist in the UI yet for some reason.
                // and the order row will not be visible until we receive that RFQ.
                continue;
              }
              const quoteRow = blotterEntitiesByID.get(order.RFQID) as QuoteRow;
              blotterEntitiesByID.set(quoteRow.RFQID, quoteRow!.enrichWith(order as OrderWithParentOrderID));
              continue;
            }

            if (order.ParentOrderID != null) {
              if (!blotterEntitiesByID.has(order.ParentOrderID)) {
                continue;
              }
              blotterEntitiesByID.set(order.OrderID, new OrderRow(order as OrderWithParentOrderID));
            }
          }

          return {
            initial: true,
            data: Array.from(blotterEntitiesByID.values()),
          };
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [careOrdersByIDObs, workingOrdersByIDObs, quotesByRFQIDObs, ordersByOrderIDObs, securitiesBySymbolRef]
  );

  const combinedErrorObs = useCombinedErrorObs(
    [careOrderErrors, quoteErrors, orderErrors, enableCareOrderNetting ? workingOrderErrors : undefined].filter(
      errorObs => errorObs != null
    )
  );
  return { dataObservable: combinedDataObs, errorObservable: combinedErrorObs };
};
